import { useForm } from '@mantine/form';
import { useLinkValidation } from '../../../modals/TaskModal/Links/useLinkValidation';
import CustomModalOverlay from '../../Custom/CustomModalOverlay/CustomModalOverlay';
import { useEffect } from 'react';
import GlobalButton from '../../../ui/GlobalButton';
import SelectInput from '../../../ui/SelectInput/SelectInput';
import TextInput from '../../../ui/TextInput/TextInput';
import { LINK_TYPE_OPTIONS } from '../../../modals/TaskModal/Links/AddLinkModal/AddLinkModal';
import TextArea from '../../../ui/TextArea/TextArea';

interface AddReferenceLinkModalProps {
  open: any;
  close: any;
  onAddLink: any;
  onEditLink?: any;
  loading?: any;
  link?: any;
}

export default function AddReferenceLinkModal({
  open,
  close,
  onAddLink,
  onEditLink,
  loading,
  link,
}: AddReferenceLinkModalProps) {
  const form = useForm({
    initialValues: {
      project_link_name: link?.project_link_name || '',
      project_link_url: link?.project_link_url || '',
      project_link_type: link?.project_link_type || '',
      project_link_description: link?.project_link_description || '',
    },
    validate: {
      project_link_name: (value: string) => {
        if (!value || value.trim() === '') {
          return 'Link title is required';
        }
        if (value.trim().length < 3) {
          return 'Title must be at least 3 characters';
        }
        if (value.length > 100) {
          return 'Title must be less than 100 characters';
        }
        return null;
      },

      project_link_url: (value: string) => {
        if (!value || value.trim() === '') {
          return 'URL is required';
        }
        try {
          const url = new URL(value.trim());
          if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return 'URL must start with http:// or https://';
          }
        } catch {
          return 'Please enter a valid URL';
        }
        return null;
      },

      project_link_type: (value: string) => {
        if (!value || value.trim() === '') {
          return 'Link type is required';
        }
        return null;
      },
      project_link_description: (value: string) => {
        if (value && value.length > 120) {
          return 'Description must be less than 500 characters';
        }
        return null;
      },
    },
    validateInputOnBlur: true,
  });

  const { detectedType } = useLinkValidation(form.values.project_link_url);

  // Update form values when link prop changes
  useEffect(() => {
    if (link) {
      form.setValues({
        project_link_name: link.project_link_name || '',
        project_link_url: link.project_link_url || '',
        project_link_type: link.project_link_type || '',
        project_link_description: link.project_link_description || '',
      });
    } else {
      form.reset();
    }
  }, [link]);

  // Auto-detect link type in real-time as URL changes
  useEffect(() => {
    if (detectedType) {
      form.setFieldValue('project_link_type', detectedType);
    }
  }, [detectedType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationResult = form.validate();

    if (!validationResult.hasErrors) {
      if (link && onEditLink) {
        onEditLink(form.values);
      } else {
        onAddLink(form.values);
      }
      form.reset();
      close();
    }
  };

  return (
    <CustomModalOverlay
      open={open}
      onClose={() => {
        close();
        form.reset();
      }}
      title={link ? 'Edit Reference Link' : 'Add Reference Link'}
      subTitle={
        'Add links to documents that provide context for this project. These will help your team and Crafter stay aligned as the project evolves.'
      }
      width="600px"
    >
      <div>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <TextInput
            label={'Enter Link Title'}
            placeholder={'Enter link title'}
            isRequired
            {...form.getInputProps('project_link_name')}
          />

          <TextInput
            label={'Enter URL'}
            placeholder={'Enter a valid url'}
            isRequired
            {...form.getInputProps('project_link_url')}
          />

          <SelectInput
            portal
            label={'Link Type'}
            placeholder="Select the link type"
            isRequired
            {...form.getInputProps('project_link_type')}
            options={LINK_TYPE_OPTIONS}
          />

          <TextArea
            label={'Enter Description (Optional)'}
            placeholder={'Enter a brief description of the link'}
            {...form.getInputProps('project_link_description')}
            height="100px"
          />
        </form>

        <div className="mt-6 mb-4 flex items-center justify-end gap-4">
          <div>
            <GlobalButton
              variant="stroke"
              onClick={() => {
                close();
                form.reset();
              }}
            >
              Cancel
            </GlobalButton>
          </div>
          <div>
            <GlobalButton
              onClick={handleSubmit}
              type={'button'}
              state={!form.isValid() || loading ? 'inactive' : 'default'}
            >
              {link ? 'Update Link' : 'Save Link'}
            </GlobalButton>
          </div>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
